import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Table, Button, Modal, Form, Input, message, Space, Tag, Popconfirm, Alert, Spin, Row, Col, Empty, Select, Switch, Upload
} from 'antd';
import { PlusOutlined, SearchOutlined, LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import JobQuillEditor from '../../quill/JobQuillEditor';
import { S3_CONFIG } from '../../config/s3';
import {
  createJob,
  deleteJob,
  updateJob,
  fetchJobs,
  toggleJobStatus
} from '../../store/features/dashboard/JobSlice';
import { debounce, beforeUploadValidation } from '../../lib/utils';

const AddJob = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [description, setDescription] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [searchText, setSearchText]

  = useState('');
  const [jobStatus, setJobStatus] = useState('');
  const [deletedFilter, setDeletedFilter] = useState(''); // New state for deleted filter
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = useRef(null);
  const dispatch = useDispatch();

  const jobState = useSelector((state) => state.jobs);
  const {
    jobs,
    loading,
    error,
    total = 0,
    currentPage = 1,
    pageSize = 10,
  } = jobState;

  const normFile = (e) => {
    return Array.isArray(e) ? e : e?.fileList;
  };

  const fetchData = useCallback((page, size, search = '', status = '', deleted = '') => {
    setIsSearching(true);
    dispatch(fetchJobs({
      page,
      limit: size || pageSize,
      search,
      job_status: status,
      deleted: deleted === '' ? undefined : deleted === 'Deleted' // Map to boolean or undefined
    })).finally(() => {
      setIsSearching(false);
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    });
  }, [dispatch, pageSize]);

  const debouncedSearch = useCallback(
    debounce((searchValue, statusValue, deletedValue) => {
      if (searchValue.length >= 2 || searchValue.length === 0) {
        fetchData(1, pageSize, searchValue, statusValue, deletedValue);
      }
    }, 500),
    [fetchData, pageSize]
  );

  const handleSearch = (value, event) => {
    setSearchText(value);
    if (event && event.type === 'change') {
      debouncedSearch(value, jobStatus, deletedFilter);
    } else {
      if (value.length >= 2 || value.length === 0) {
        fetchData(1, pageSize, value, jobStatus, deletedFilter);
      }
    }
  };

  const handleClearSearch = () => {
    setSearchText('');
    fetchData(1, pageSize, '', jobStatus, deletedFilter);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleJobStatusChange = (value) => {
    setJobStatus(value);
    fetchData(1, pageSize, searchText, value, deletedFilter);
  };

  const handleDeletedFilterChange = (value) => {
    setDeletedFilter(value);
    fetchData(1, pageSize, searchText, jobStatus, value);
  };

  useEffect(() => {
    fetchData(currentPage, pageSize, searchText, jobStatus, deletedFilter);
  }, [currentPage, pageSize, fetchData]);

  const handlePageChange = (page, size) => {
    fetchData(page, size, searchText, jobStatus, deletedFilter);
  };

  const resetModal = () => {
    setModalVisible(false);
    setDescription('');
    setIsEditMode(false);
    setCurrentJobId(null);
    form.resetFields();
    setSubmitLoading(false);
  };

  const handleDelete = async (jobId) => {
    try {
      await dispatch(deleteJob(jobId)).unwrap();
      message.success('Job deleted successfully!');
      if (jobs.length === 1 && currentPage > 1) {
        fetchData(currentPage - 1, pageSize, searchText, jobStatus, deletedFilter);
      } else {
        fetchData(currentPage, pageSize, searchText, jobStatus, deletedFilter);
      }
    } catch (error) {
      message.error(error.message || 'Failed to delete job');
    }
  };

  const handleEdit = (job) => {
    setIsEditMode(true);
    setCurrentJobId(job.id);
    setDescription(job.description || '');
    setModalVisible(true);

    const imageFields = {};
    if (Array.isArray(job.images)) {
      job.images.forEach((image, index) => {
        if (image && typeof image === 'object' && image.url && Number.isInteger(image.order)) {
          const imageKey = `image${image.order + 1}`; // Map order (0-based) to image1, image2
          imageFields[imageKey] = [{
            uid: `-${index}`,
            name: image.url.split('/').pop(),
            status: 'done',
            url: `${S3_CONFIG.publicUrl}/${image.url}`,
          }];
        }
      });
    }

    for (let i = 1; i <= 2; i++) {
      if (!imageFields[`image${i}`]) {
        imageFields[`image${i}`] = [];
      }
    }

    form.setFieldsValue({
      title: job.title || '',
      location: job.location || '',
      type: job.type || '',
      level: job.level || undefined,
      is_active: job.is_active !== undefined ? job.is_active : true,
      ...imageFields,
    });
  };

  const handleToggleStatus = async (jobId, currentStatus) => {
    try {
      await dispatch(toggleJobStatus(jobId)).unwrap();
      message.success(`Job status ${currentStatus ? 'deactivated' : 'activated'} successfully!`);
      fetchData(currentPage, pageSize, searchText, jobStatus, deletedFilter);
    } catch (error) {
      message.error(error.message || 'Failed to toggle job status');
    }
  };

  const handleJobSubmit = async (values, isEdit = false) => {
    try {
      setSubmitLoading(true);

      const requiredFields = [
        { field: 'title', message: 'Please add title to the job' },
        { field: 'location', message: 'Please add location to the job' },
        { field: 'type', message: 'Please select job type' },
        { field: 'description', message: 'Please add description to the job' },
      ];

      for (const item of requiredFields) {
        if (item.field === 'description') {
          if (!description?.trim()) {
            message.error(item.message);
            setSubmitLoading(false);
            return;
          }
        } else if (!values[item.field]?.trim()) {
          message.error(item.message);
          setSubmitLoading(false);
          return;
        }
      }

      const imageFields = ['image1', 'image2'];
      const missingImages = imageFields.filter(field => !values[field] || values[field].length === 0);
      if (missingImages.length > 0) {
        message.error(`Please upload both images (missing: ${missingImages.map(f => f.replace('image', 'Image ')).join(', ')})`);
        setSubmitLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append('title', values.title);
      formData.append('location', values.location);
      formData.append('type', values.type);
      if (values.level) formData.append('level', values.level);
      formData.append('description', description);
      formData.append('is_active', values.is_active.toString());

      const newFiles = [];
      const fileOrders = [];
      const existingImages = [];

      imageFields.forEach((fieldName, index) => {
        const fileList = values[fieldName];
        if (fileList && fileList[0]) {
          const fileInfo = fileList[0];
          const order = index; // 0-based index for order (0, 1)

          if (fileInfo.originFileObj) {
            newFiles.push(fileInfo.originFileObj);
            fileOrders.push(order);
          } else if (fileInfo.url) {
            const cleanUrl = fileInfo.url.replace(`${S3_CONFIG.publicUrl}/`, '');
            existingImages.push({ url: cleanUrl, order });
          }
        }
      });

      if (newFiles.length + existingImages.length !== 2) {
        message.error('Exactly 2 images are required (new or existing)');
        setSubmitLoading(false);
        return;
      }
      if (fileOrders.length > 0) {
        const uniqueOrders = new Set(fileOrders);
        if (uniqueOrders.size !== fileOrders.length || fileOrders.some(order => order < 0 || order > 1)) {
          message.error('Invalid file orders: must be unique integers between 0 and 1');
          setSubmitLoading(false);
          return;
        }
      }
      if (existingImages.length > 0) {
        const uniqueOrders = new Set(existingImages.map(img => img.order));
        if (uniqueOrders.size !== existingImages.length || existingImages.some(img => img.order < 0 || img.order > 1)) {
          message.error('Invalid existing image positions: must be unique integers between 0 and 1');
          setSubmitLoading(false);
          return;
        }
      }

      newFiles.forEach(file => {
        formData.append('files', file);
      });
      if (fileOrders.length > 0) {
        formData.append('fileOrders', JSON.stringify(fileOrders));
      }
      if (existingImages.length > 0) {
        formData.append('existingImagePositions', JSON.stringify(existingImages));
      }

      console.log('FormData contents:');
      for (let [key, value] of formData.entries()) {
        console.log(`${key}: ${value}`);
      }

      let response;
      if (isEdit) {
        response = await dispatch(updateJob({ jobId: currentJobId, formData })).unwrap();
      } else {
        response = await dispatch(createJob(formData)).unwrap();
      }

      if (response && response.error) {
        throw new Error(response.message || 'An error occurred');
      }

      message.success(`Job ${isEdit ? 'updated' : 'created'} successfully!`);
      resetModal();
      fetchData(currentPage, pageSize, searchText, jobStatus, deletedFilter);
    } catch (error) {
      console.error('Submission error:', error);
      const errorMessage = Array.isArray(error.message) ? error.message.join(', ') : error.message || `Failed to ${isEdit ? 'update' : 'create'} job`;
      message.error(errorMessage);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSubmit = () => {
    form.validateFields()
      .then(values => {
        handleJobSubmit(values, isEditMode);
      })
      .catch(error => {
        console.error('Validation error:', error);
        message.error('Please fill all required fields');
        setSubmitLoading(false);
      });
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 200,
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      width: 150,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: type => <Tag color="blue">{type || 'N/A'}</Tag>,
    },
    {
      title: 'Level',
      dataIndex: 'level',
      key: 'level',
      width: 120,
      render: level => <Tag color="purple">{level || 'N/A'}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'status',
      width: 120,
      render: (is_active, record) => (
        <Popconfirm
          title={`Are you sure to ${is_active ? 'deactivate' : 'activate'} this job?`}
          onConfirm={() => handleToggleStatus(record.id, is_active)}
          okText="Yes"
          cancelText="No"
          disabled={record.deletedAt} // Disable toggle for deleted jobs
        >
          <Tag color={is_active && !record.deletedAt ? 'green' : 'red'} style={{ cursor: record.deletedAt ? 'not-allowed' : 'pointer' }}>
            {record.deletedAt ? 'Deleted' : is_active ? 'Active' : 'Inactive'}
          </Tag>
        </Popconfirm>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'date',
      width: 120,
      render: date => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Deleted At',
      dataIndex: 'deletedAt',
      key: 'deletedAt',
      width: 120,
      render: deletedAt => deletedAt ? new Date(deletedAt).toLocaleDateString() : 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button onClick={() => handleEdit(record)} disabled={record.deletedAt}>Edit</Button>
          <Popconfirm
            title="Are you sure to delete this job?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            disabled={record.deletedAt} // Disable delete for already deleted jobs
          >
            <Button danger disabled={record.deletedAt}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading && jobs.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Jobs"
        description={error}
        type="error"
        showIcon
        style={{ margin: '24px' }}
      />
    );
  }

  return (
    <div style={{
      padding: '24px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%'
    }}>
      <h2 style={{ marginBottom: '16px' }}>Jobs Management</h2>

      <div style={{
        width: '100%',
        maxWidth: '1200px',
        marginBottom: '16px'
      }}>
        <Row justify="space-between" align="middle" gutter={16}>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setIsEditMode(false);
                setModalVisible(true);
              }}
            >
              Add Job
            </Button>
          </Col>
          <Col>
            <Space>
              <Select
                style={{ width: 200 }}
                placeholder="Filter by job status"
                allowClear
                onChange={handleJobStatusChange}
                value={jobStatus}
              >
                <Select.Option value="Active">Active</Select.Option>
                <Select.Option value="Inactive">Inactive</Select.Option>
              </Select>
              <Select
                style={{ width: 200 }}
                placeholder="Filter by deleted status"
                allowClear
                onChange={handleDeletedFilterChange}
                value={deletedFilter}
              >
                <Select.Option value="">All</Select.Option>
                <Select.Option value="Deleted">Deleted</Select.Option>
                <Select.Option value="Not Deleted">Not Deleted</Select.Option>
              </Select>
              <Input.Search
                ref={searchInputRef}
                placeholder="Search jobs..."
                allowClear
                enterButton={isSearching ? <LoadingOutlined /> : <SearchOutlined />}
                size="large"
                style={{ width: 300 }}
                value={searchText}
                onChange={(e) => handleSearch(e.target.value, e)}
                onSearch={(value) => handleSearch(value, { type: 'search' })}
                onClear={handleClearSearch}
                loading={isSearching}
              />
            </Space>
          </Col>
        </Row>
      </div>

      <div style={{ width: '100%', maxWidth: '1200px' }}>
        {jobs.length === 0 && !loading ? (
          <Empty
            description={
              searchText || jobStatus || deletedFilter
                ? `No jobs found for "${searchText || ''}" ${jobStatus ? `with status ${jobStatus}` : ''} ${deletedFilter ? `and deleted status ${deletedFilter}` : ''}`
                : 'No jobs available'
            }
            style={{ margin: '40px 0' }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={jobs}
            loading={loading}
            rowKey="id"
            bordered
            scroll={{
              x: 'max-content',
              y: 'calc(100vh - 340px)'
            }}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              onChange: handlePageChange,
              onShowSizeChange: handlePageChange,
              showSizeChanger: true,
              pageSizeOptions: ['5', '10', '15', '20'],
              showTotal: (total, range) => (
                <span>
                  Showing {range[0]}-{range[1]} of {total} records
                </span>
              ),
              position: ['bottomCenter'],
            }}
            style={{ width: '100%' }}
          />
        )}
      </div>

      <Modal
        title={isEditMode ? 'Edit Job' : 'Create Job'}
        open={modalVisible}
        onCancel={resetModal}
        onOk={handleSubmit}
        width={800}
        confirmLoading={submitLoading}
        okButtonProps={{ loading: submitLoading }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Please input the job title!' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="location"
            label="Location"
            rules={[{ required: true, message: 'Please input the location!' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true, message: 'Please select the job type!' }]}
          >
            <Select>
              <Select.Option value="Full-time">Full-time</Select.Option>
              <Select.Option value="Part-time">Part-time</Select.Option>
              <Select.Option value="Contract">Contract</Select.Option>
              <Select.Option value="Internship-Paid">Internship-Paid</Select.Option>
              <Select.Option value="Internship-Unpaid">Internship-Unpaid</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="level"
            label="Level"
          >
            <Select allowClear>
              <Select.Option value="Entry">Entry</Select.Option>
              <Select.Option value="Mid">Mid</Select.Option>
              <Select.Option value="Senior">Senior</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Active Status"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>

          <Form.Item
            label="Description"
            required
            validateStatus={!description ? 'error' : ''}
            help={!description ? 'Please add description to the job' : ''}
          >
            <JobQuillEditor
              value={description}
              onChange={setDescription}
              resetTemplate={isEditMode}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="image1"
                label="card image (453x256)"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                rules={[{
                  required: true,
                  validator: (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.reject('Please upload the card image');
                    }
                    return Promise.resolve();
                  }
                }]}
              >
                <Upload
                  customRequest={({ file, onSuccess }) => {
                    setTimeout(() => {
                      onSuccess({ url: URL.createObjectURL(file) });
                    }, 1000);
                  }}
                  listType="picture-card"
                  maxCount={1}
                  beforeUpload={(file) => beforeUploadValidation(file, 'card image', 5, 453, 256)}
                  accept="image/*"
                >
                  <div>
                    <UploadOutlined />
                    <div>Upload</div>
                  </div>
                </Upload>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="image2"
                label="banner image (1920x1080)"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                rules={[{
                  required: true,
                  validator: (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.reject('Please upload the banner image');
                    }
                    return Promise.resolve();
                  }
                }]}
              >
                <Upload
                  customRequest={({ file, onSuccess }) => {
                    setTimeout(() => {
                      onSuccess({ url: URL.createObjectURL(file) });
                    }, 1000);
                  }}
                  listType="picture-card"
                  maxCount={1}
                  beforeUpload={(file) => beforeUploadValidation(file, 'banner image', 5, 1920, 1080)}
                  accept="image/*"
                >
                  <div>
                    <UploadOutlined />
                    <div>Upload</div>
                  </div>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default AddJob;